import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, CircularProgress, MenuItem, TextField } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import api from '../../api';

const PunctualitySection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const currentDate = new Date();
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());
  
  const empId = localStorage.getItem('emp_id');

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/punctuality/${empId}?month=${month}&year=${year}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, [empId, month, year]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress size={24} /></Box>;
  if (data?.error) return <Typography color="error" variant="body2" sx={{ p: 2 }}>{data.error}</Typography>;
  if (!data) return null;

  // Gauge Color Logic
  let ratioColor = '#d32f2f'; // Red (<= 90)
  if (data.ratio >= 96) ratioColor = '#2e7d32'; // Green
  else if (data.ratio > 90) ratioColor = '#ed6c02'; // Amber

  const gaugeData = [{ name: 'Ratio', value: data.ratio, fill: ratioColor }];

  const KPICard = ({ title, actual, expected }) => (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: '#e0e0e0', height: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}>{title}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>{actual}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>/ {expected}</Typography>
      </Box>
    </Paper>
  );

  return (
    <Box>
      {/* Header & Filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a' }}>Calculation Period</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{data.period_str}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField select size="small" value={month} onChange={(e) => setMonth(e.target.value)} sx={{ width: 120, bgcolor: 'white' }}>
            {[...Array(12)].map((_, i) => (<MenuItem key={i+1} value={i+1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</MenuItem>))}
          </TextField>
          <TextField select size="small" value={year} onChange={(e) => setYear(e.target.value)} sx={{ width: 100, bgcolor: 'white' }}>
            {[year-1, year, year+1].map(y => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
          </TextField>
        </Box>
      </Box>

      {/* KPI Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <KPICard title="Total Worked Hours" actual={data.actual_worked_str} expected={data.expected_worked_str} />
        </Grid>
        <Grid item xs={12} md={4}>
          <KPICard title="Total Office Hours" actual={data.actual_office_str} expected={data.expected_office_str} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#e0e0e0', height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>Punctuality Score</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Based on {data.level}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: ratioColor }}>{data.ratio}%</Typography>
            </Box>
            <Box sx={{ width: 80, height: 80 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" barSize={8} data={gaugeData} startAngle={90} endAngle={-270}>
                  <RadialBar background clockWise dataKey="value" cornerRadius={10} />
                </RadialBarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts Row - Using Flexbox to force side-by-side layout */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* 2/3 Width for Daily Performance Trends */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 66.666%' }, minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: '#e0e0e0', height: 350 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 2 }}>Daily Performance Trends</Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily_data} margin={{ top: 5, right: 0, left: -20, bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis dataKey="date" tick={{fontSize: 10}} tickMargin={10} />
                <YAxis tick={{fontSize: 10}} />
                <ChartTooltip contentStyle={{ fontSize: '12px', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                
                {data.level.includes("Level 1") ? (
                  <>
                    <Line type="monotone" dataKey="worked_hrs" name="Actual Worked Hrs" stroke="#1976d2" strokeWidth={3} dot={{r: 4}} activeDot={{r: 6}} />
                    <Line type="monotone" dataKey="expected_worked" name="Expected Hrs" stroke="#9e9e9e" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                  </>
                ) : (
                  <>
                    <Line type="monotone" dataKey="office_hrs" name="Actual Office Hrs" stroke="#9c27b0" strokeWidth={3} dot={{r: 4}} activeDot={{r: 6}} />
                    <Line type="monotone" dataKey="expected_office" name="Expected Hrs" stroke="#9e9e9e" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                  </>
                )}
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Box>

        {/* 1/3 Width for Attendance Breakdown */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 33.333%' }, minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: '#e0e0e0', height: 350 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', mb: 2 }}>Attendance Breakdown</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
              {data.trend_summary.map((item) => (
                <Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: item.fill }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242' }}>{item.name}</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>{item.value} Days</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default PunctualitySection;